import "./colorpicker.scss";
import {
    type FC,
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import { HexColorInput, type RgbaColor, RgbaColorPicker } from "react-colorful";
import PrimaryButton from "../Buttons/PrimaryButton";

interface Props {
    onChange?: (result: string) => void;
    value?: string;
    id?: string;
    className?: string;
    clearable?: boolean;
}

const format = (number: number): string => {
    const hex = number.toString(16);

    return hex.length < 2 ? `0${hex}` : hex;
};

const rgbaToHex = ({ r, g, b, a }: RgbaColor): string => {
    if(isNaN(r) || isNaN(g) || isNaN(b) || isNaN(a)) {
        return "#00000000";
    }

    return `#${format(r)}${format(g)}${format(b)}${format(Math.round(a * 255))}`;
};

const hexToRgba = (hex: string): RgbaColor => {
    if(hex[0] === "#") hex = hex.substring(1);

    if(hex.length < 6) {
        return {
            r: parseInt(hex[0] + hex[0], 16),
            g: parseInt(hex[1] + hex[1], 16),
            b: parseInt(hex[2] + hex[2], 16),
            a: 1,
        };
    }

    let alpha = Math.round(parseInt(hex.substring(6, 8), 16) / 255 * 100) / 100;

    if(isNaN(alpha)) {
        alpha = 1;
    }

    return {
        r: parseInt(hex.substring(0, 2), 16),
        g: parseInt(hex.substring(2, 4), 16),
        b: parseInt(hex.substring(4, 6), 16),
        a: alpha,
    };
};

const ColorPicker: FC<Props> = ({
    id,
    className,
    onChange,
    value,
}) => {
    const [ color, setColor ] = useState(() => {
        if(typeof value === "undefined") {
            return undefined;
        }

        return hexToRgba(value);
    });

    const [ visible, setVisible ] = useState(false);
    const popupRef = useRef<HTMLDivElement | null>(null);
    const pickerRef = useRef<HTMLDivElement | null>(null);

    const onInternalChange = useCallback((color: RgbaColor) => {
        setColor(color);

        if(onChange) {
            onChange(rgbaToHex(color));
        }
    }, [ onChange ]);

    const detectBackgroundClick = useCallback((e: PointerEvent) => {
        if(pickerRef.current?.contains(e.target as HTMLElement)) {
            return;
        }

        setVisible(false);
    }, []);

    useEffect(() => {
        if(typeof value === "string" && typeof onChange === "function") {
            setColor(hexToRgba(value));
        }
    }, [ value, onChange ]);

    useEffect(() => {
        if(visible) {
            window.addEventListener("click", detectBackgroundClick);
        } else {
            window.removeEventListener("click", detectBackgroundClick);
        }

        return () => {
            window.removeEventListener("click", detectBackgroundClick);
        };
    }, [ visible, detectBackgroundClick ]);

    return (
        <div
            id={id}
            className={`${className} color-picker relative border border-black`}
            ref={pickerRef}
        >
            <div
                className="border-4 p-4 w-full h-full"
                onClick={() => setVisible(prevState => !prevState)}
                style={{
                    backgroundColor: typeof color === "undefined" ?
                        "transparent" :
                        rgbaToHex(color),
                }}
            />
            {visible && (
                <div className="absolute top-full shadow-2xl z-10 border-2 min-w-[250px]" ref={popupRef}>
                    <RgbaColorPicker
                        color={
                            (typeof onChange === "undefined" || typeof value === "undefined") ?
                                color :
                                hexToRgba(value)
                        }
                        onChange={onInternalChange}
                    />
                    <div className="flex">
                        <HexColorInput
                            prefixed
                            alpha
                            color={
                                (typeof onChange === "undefined" && typeof color !== "undefined") ?
                                    rgbaToHex(color) :
                                    value
                            }
                            onChange={color => onInternalChange(hexToRgba(color))}
                        />
                        <PrimaryButton onClick={() => setVisible(false)}>
                            Done
                        </PrimaryButton>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ColorPicker;
